import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Button as AntButton,
  Card as AntCard,
  Col as AntCol,
  DatePicker as AntDatePicker,
  Input as AntInput,
  Row as AntRow,
  Select as AntSelect,
  Space,
  Switch as AntSwitch,
  Typography,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFileUpload } from 'hooks/useFileUpload';
import RichTextEditor from "components/common/RichTextEditor";

const { Text } = Typography;

const EditableField = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  type = 'text',
  options,
  isSaving,
  isEditMode,
  disabled,
  placeholder,
  appendContent,
}) => {
  const selectPlaceholder = placeholder || 'Select...';
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  const normalizedOptions =
    options?.map((opt) => {
      if (typeof opt === 'string') return { label: opt, value: opt };
      if (typeof opt === 'object' && opt !== null) return opt;
      return null;
    }).filter(Boolean) || [];

  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);

  const renderValueInput = (currentValue, setValue, inputDisabled) => {
    if (type === 'date') {
      const parsedDate = currentValue ? dayjs(currentValue) : null;
      const dateValue = parsedDate?.isValid?.() ? parsedDate : null;

      return (
        <AntDatePicker
          style={{ width: '100%' }}
          value={dateValue}
          placeholder={placeholder || 'YYYY-MM-DD'}
          disabled={inputDisabled}
          onChange={(nextDate) => setValue(nextDate ? nextDate.format('YYYY-MM-DD') : '')}
        />
      );
    }

    if (type === 'search-select' || type === 'select') {
      return (
        <AntSelect
          showSearch={type === 'search-select'}
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder={selectPlaceholder}
          options={normalizedOptions}
          value={currentValue || undefined}
          disabled={inputDisabled}
          allowClear
          onChange={(nextValue) => setValue(nextValue || '')}
        />
      );
    }

    return (
      <AntInput
        type={type}
        value={currentValue || ''}
        disabled={inputDisabled}
        placeholder={placeholder}
        suffix={appendContent || null}
        onChange={(e) => setValue(e.target.value)}
      />
    );
  };

  if (!isEditMode || !onSave) {
    return (
      <div>
        <Text strong>{label}</Text>
        <div style={{ marginTop: 4 }}>
          {renderValueInput(
            value,
            (nextValue) => onChange?.({ target: { name, value: nextValue } }),
            disabled
          )}
        </div>
        {error && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {error}
          </Text>
        )}
      </div>
    );
  }

  const toggleEdit = async () => {
    if (isEditing) {
      if (internalValue === (originalValue || '')) {
        toast.info('No changes detected');
        setIsEditing(false);
        return;
      }

      try {
        await onSave(name, internalValue);
        setIsEditing(false);
      } catch (err) {
        toast.error(`Failed to update ${label}: ${err.message}`);
      }
      return;
    }

    setIsEditing(true);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <Text strong>{label}</Text>
        <AntButton type="link" size="small" loading={isSaving} onClick={toggleEdit}>
          {isEditing ? 'Save' : 'Edit'}
        </AntButton>
      </div>

      {renderValueInput(internalValue, setInternalValue, !isEditing)}

      {error && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {error}
        </Text>
      )}
    </div>
  );
};

const DocumentUploadSection = ({
  label,
  existingDocuments = [],
  onUpload,
  onRemove,
  isEditMode,
  target,
  options = [],
}) => {
  const [documentType, setDocumentType] = useState('');
  const [frontFileList, setFrontFileList] = useState([]);
  const [backFileList, setBackFileList] = useState([]);

  const handleSubmit = () => {
    if (!documentType) return toast.error('Document type is required');
    if (!frontFileList.length || !backFileList.length)
      return toast.error('Front and back files are required');

    const frontFile = frontFileList[0]?.originFileObj || frontFileList[0];
    const backFile = backFileList[0]?.originFileObj || backFileList[0];

    onUpload([frontFile, backFile], documentType, target);

    setDocumentType('');
    setFrontFileList([]);
    setBackFileList([]);
  };

  return (
    <AntCard
      size="small"
      style={{ marginBottom: 24 }}
      title={<span className="fw-semibold">{label}</span>}
    >
      {existingDocuments.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Text strong>Existing Documents</Text>
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            {existingDocuments.map((doc) => (
              <div
                key={doc.id || doc.originalName}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <div>
                  <Text>{doc.originalName}</Text>
                  <div>
                    <Text type="secondary">
                      {doc.documentType} • {doc.side}
                    </Text>
                  </div>
                </div>
                {isEditMode && (
                  <AntButton
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove?.(doc.id, target)}
                  >
                    Remove
                  </AntButton>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <div>
          <Text strong>Document Type</Text>
          <AntSelect
            style={{ width: '100%', marginTop: 4 }}
            value={documentType || undefined}
            placeholder="Select document type"
            options={options}
            onChange={(value) => setDocumentType(value)}
          />
        </div>

        <AntRow gutter={[12, 12]}>
          <AntCol xs={24} md={12}>
            <Text strong>Front Document</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                fileList={frontFileList}
                onChange={({ fileList }) => setFrontFileList(fileList.slice(-1))}
              >
                <AntButton icon={<UploadOutlined />}>Upload Front</AntButton>
              </Upload>
            </div>
          </AntCol>
          <AntCol xs={24} md={12}>
            <Text strong>Back Document</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                fileList={backFileList}
                onChange={({ fileList }) => setBackFileList(fileList.slice(-1))}
              >
                <AntButton icon={<UploadOutlined />}>Upload Back</AntButton>
              </Upload>
            </div>
          </AntCol>
        </AntRow>

        <div style={{ textAlign: 'right' }}>
          <AntButton type="primary" onClick={handleSubmit}>
            Submit Documents
          </AntButton>
        </div>
      </Space>
    </AntCard>
  );
};

const WorkAuthorizationUploadSection = ({
  label,
  existingDocuments = [],
  onUpload,
  onRemove,
  isEditMode,
  target,
  options = [],
  capabilityOptions = [],
  defaultValues = {},
  onChange,
}) => {
  const [workAuthorizationType, setWorkAuthorizationType] = useState('');
  const [capability, setCapability] = useState('');
  const [canWork, setCanWork] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [hasRestrictions, setHasRestrictions] = useState(false);
  const [expirationDate, setExpirationDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [frontFileList, setFrontFileList] = useState([]);
  const [backFileList, setBackFileList] = useState([]);

  useEffect(() => {
    setWorkAuthorizationType(
      defaultValues?.type || defaultValues?.workAuthorizationType || ''
    );
    setCapability(defaultValues?.capability || '');
    setCanWork(
      typeof defaultValues?.canWork === 'boolean' ? defaultValues.canWork : false
    );
    setRequiresVerification(
      typeof defaultValues?.requiresVerification === 'boolean'
        ? defaultValues.requiresVerification
        : false
    );
    setHasRestrictions(
      typeof defaultValues?.hasRestrictions === 'boolean'
        ? defaultValues.hasRestrictions
        : false
    );
    setExpirationDate(
      defaultValues?.expirationDate ? dayjs(defaultValues.expirationDate) : null
    );
    setNotes(defaultValues?.notes || '');
  }, [defaultValues]);

  const emitChange = (overrides = {}) => {
    onChange?.({
      capability,
      type: workAuthorizationType,
      workAuthorizationType,
      canWork,
      requiresVerification,
      hasRestrictions,
      expirationDate: expirationDate ? expirationDate.format('YYYY-MM-DD') : null,
      notes,
      ...overrides,
    });
  };

  const handleSubmit = () => {
    if (!workAuthorizationType) {
      return toast.error('Work authorization type is required');
    }
    if (!capability) {
      return toast.error('Capability is required');
    }
    if (!frontFileList.length || !backFileList.length) {
      return toast.error('Front and back files are required');
    }

    const frontFile = frontFileList[0]?.originFileObj || frontFileList[0];
    const backFile = backFileList[0]?.originFileObj || backFileList[0];

    onUpload(
      [frontFile, backFile],
      {
        capability,
        type: workAuthorizationType,
        workAuthorizationType,
        canWork,
        requiresVerification,
        hasRestrictions,
        expirationDate: expirationDate ? expirationDate.format('YYYY-MM-DD') : null,
        notes,
      },
      target
    );

    setFrontFileList([]);
    setBackFileList([]);
  };

  return (
    <AntCard
      size="small"
      style={{ marginBottom: 24 }}
      title={<span className="fw-semibold">{label}</span>}
    >
      {existingDocuments.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Existing Documents</Text>
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            {existingDocuments.map((doc) => (
              <div
                key={doc.id || doc.originalName}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <div>
                  <Text>{doc.originalName}</Text>
                  <div>
                    <Text type="secondary">
                      {doc.documentType} • {doc.side}
                    </Text>
                  </div>
                </div>
                {isEditMode && (
                  <AntButton
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove?.(doc.id, target)}
                  >
                    Remove
                  </AntButton>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <AntRow gutter={12}>
          <AntCol xs={24} md={12}>
            <Text strong>Work Authorization Type</Text>
            <AntSelect
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select work authorization type"
              value={workAuthorizationType || undefined}
              options={options}
              onChange={(value) => {
                setWorkAuthorizationType(value);
                emitChange({ type: value, workAuthorizationType: value });
              }}
              showSearch
              optionFilterProp="label"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <Text strong>Capability</Text>
            <AntSelect
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select capability"
              value={capability || undefined}
              options={capabilityOptions}
              onChange={(value) => {
                setCapability(value);
                emitChange({ capability: value });
              }}
              showSearch
              optionFilterProp="label"
            />
          </AntCol>
        </AntRow>

        <AntRow gutter={16}>
          <AntCol xs={24} md={8}>
            <div
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 10,
                padding: '6px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                background: '#fafafa',
              }}
            >
              <Text style={{ margin: 0, whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>Can Work</Text>
              <AntSwitch
                checked={canWork}
                checkedChildren="YES"
                unCheckedChildren="NO"
                style={{ minWidth: 56 }}
                onChange={(checked) => {
                  setCanWork(checked);
                  emitChange({ canWork: checked });
                }}
              />
            </div>
          </AntCol>

          <AntCol xs={24} md={8}>
            <div
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 10,
                padding: '6px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                background: '#fafafa',
              }}
            >
              <Text style={{ margin: 0, whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                Requires Verification
              </Text>
              <AntSwitch
                checked={requiresVerification}
                checkedChildren="YES"
                unCheckedChildren="NO"
                style={{ minWidth: 56 }}
                onChange={(checked) => {
                  setRequiresVerification(checked);
                  emitChange({ requiresVerification: checked });
                }}
              />
            </div>
          </AntCol>

          <AntCol xs={24} md={8}>
            <div
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 10,
                padding: '6px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                background: '#fafafa',
              }}
            >
              <Text style={{ margin: 0, whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>Has Restrictions</Text>
              <AntSwitch
                checked={hasRestrictions}
                checkedChildren="YES"
                unCheckedChildren="NO"
                style={{ minWidth: 56 }}
                onChange={(checked) => {
                  setHasRestrictions(checked);
                  emitChange({ hasRestrictions: checked });
                }}
              />
            </div>
          </AntCol>
        </AntRow>

        <AntRow gutter={12}>
          <AntCol xs={24} md={12}>
            <Text strong>Expiration Date</Text>
            <AntDatePicker
              style={{ width: '100%', marginTop: 4 }}
              value={expirationDate}
              onChange={(value) => {
                setExpirationDate(value);
                emitChange({ expirationDate: value ? value.format('YYYY-MM-DD') : null });
              }}
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <Text strong>Notes</Text>
            <div style={{ marginTop: 4 }}>
              <RichTextEditor
                value={notes}
                onChange={(nextNotes) => {
                  setNotes(nextNotes);
                  emitChange({ notes: nextNotes });
                }}
                placeholder="Any work authorization notes"
                height="150px"
              />
            </div>
          </AntCol>
        </AntRow>

        <AntRow gutter={12}>
          <AntCol xs={24} md={12}>
            <Text strong>Front Document</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                fileList={frontFileList}
                onChange={({ fileList }) => setFrontFileList(fileList.slice(-1))}
              >
                <AntButton icon={<UploadOutlined />}>Upload Front</AntButton>
              </Upload>
            </div>
          </AntCol>

          <AntCol xs={24} md={12}>
            <Text strong>Back Document</Text>
            <div style={{ marginTop: 4 }}>
              <Upload
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                fileList={backFileList}
                onChange={({ fileList }) => setBackFileList(fileList.slice(-1))}
              >
                <AntButton icon={<UploadOutlined />}>Upload Back</AntButton>
              </Upload>
            </div>
          </AntCol>
        </AntRow>

        <div style={{ textAlign: 'right' }}>
          <AntButton
            type="primary"
            onClick={handleSubmit}
            disabled={!workAuthorizationType || !capability}
          >
            Submit Work Authorization
          </AntButton>
        </div>
      </Space>
    </AntCard>
  );
};

const PersonalForm = ({
  formData = {},
  errors = {},
  handleInputChange,
  userId,
  patchProfileProperties,
  refetch,
  isEditMode,
  onIdentityFilesSelected,
  onWorkAuthFilesSelected,
  onIdentityTypeChange,
  onWorkAuthTypeChange,
  onWorkAuthorizationChange,
}) => {
  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [showTaxId, setShowTaxId] = useState(false);
  const {
    uploadFile,
    uploading,
    error: uploadError,
    submitIdentityVerification,
    submitWorkAuthorization,
  } = useFileUpload();

  const [uploadedFiles, setUploadedFiles] = useState({
    identity: [],
    workAuth: [],
  });

  useEffect(() => {
    const keys = ['birthDate', 'gender', 'ssn', 'taxIdType', 'taxId', 'status'];
    const orig = {};
    keys.forEach((k) => {
      orig[k] = formData[k] || '';
    });
    setOriginalValues(orig);

    if (userId && isEditMode) {
      const mapDocs = (docs = []) =>
        docs.map((doc) => ({
          id: doc.id,
          documentType: doc.documentType || 'UNKNOWN',
          originalName: doc.originalName || 'Unknown',
          side: doc.side || 'UNKNOWN',
          previewUrl: doc.previewUrl || null,
        }));

      setUploadedFiles({
        identity: mapDocs(formData.userIdentityVerification?.documents || []),
        workAuth: mapDocs(formData.userWorkAuthorization?.documents || []),
      });
    }
  }, [formData, userId, isEditMode]);

  const handleFieldSave = async (fieldName, value) => {
    if (!userId) return;

    setSavingFields((prev) => ({ ...prev, [fieldName]: true }));
    try {
      await patchProfileProperties(userId, [{ name: fieldName, value }]);
      handleInputChange({ target: { name: fieldName, value } });
      if (refetch) await refetch();
      toast.success(`${fieldName} updated`);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSavingFields((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
  };

  const handleUpload = async (files, documentData, target) => {
    if (files.length !== 2) {
      return toast.error('Both front and back are required');
    }

    const isIdentityUpload = target === 'identity';
    const resolvedWorkAuthPayload =
      typeof documentData === 'string'
        ? {
            type: documentData,
            workAuthorizationType: documentData,
          }
        : documentData || {};

    const resolvedDocumentType = isIdentityUpload
      ? documentData
      : resolvedWorkAuthPayload.type ||
        resolvedWorkAuthPayload.workAuthorizationType ||
        '';

    if (userId) {
      try {
        toast.info('Uploading documents...');

        const uploadedFileIds = [];
        const newDocs = [];

        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          const isImg = /\.(jpe?g|png|webp|gif|heic)$/i.test(file.name);
          const result = await uploadFile(userId, file, {
            purpose: 'USER_PROFILE',
            fileType: isImg ? 'PHOTO' : 'DOCUMENT',
          });

          const fileId = result.fileId;
          uploadedFileIds.push(fileId);

          newDocs.push({
            id: fileId,
            documentType: resolvedDocumentType,
            originalName: file.name,
            side: i === 0 ? 'FRONT' : 'BACK',
          });
        }

        setUploadedFiles((prev) => ({
          ...prev,
          [target]: [...prev[target], ...newDocs],
        }));

        if (target === 'identity') {
          await submitIdentityVerification(userId, resolvedDocumentType, uploadedFileIds);
        } else {
          await submitWorkAuthorization(userId, {
            ...resolvedWorkAuthPayload,
            type:
              resolvedWorkAuthPayload.type ||
              resolvedWorkAuthPayload.workAuthorizationType,
            workAuthorizationType:
              resolvedWorkAuthPayload.workAuthorizationType ||
              resolvedWorkAuthPayload.type,
            idDocumentIds: uploadedFileIds,
          });
        }

        toast.success('Documents uploaded and submitted');
        if (refetch) await refetch();
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Upload failed');
      }
      return;
    }

    if (target === 'identity') {
      onIdentityFilesSelected?.(files);
      onIdentityTypeChange?.(resolvedDocumentType);
      toast.info('Identity documents attached. They will upload on Create User.');
      return;
    }

    if (target === 'workAuth') {
      const payload = {
        ...resolvedWorkAuthPayload,
        type:
          resolvedWorkAuthPayload.type ||
          resolvedWorkAuthPayload.workAuthorizationType,
        workAuthorizationType:
          resolvedWorkAuthPayload.workAuthorizationType ||
          resolvedWorkAuthPayload.type,
      };

      onWorkAuthFilesSelected?.(files);
      onWorkAuthTypeChange?.(payload.workAuthorizationType || '');
      onWorkAuthorizationChange?.(payload);
      toast.info('Work authorization documents attached. They will upload on Create User.');
    }
  };

  const handleRemove = (fileId, target) => {
    if (!userId) return;

    setUploadedFiles((prev) => ({
      ...prev,
      [target]: prev[target].filter((d) => d.id !== fileId),
    }));

    toast.info('Document removed');
  };

  const identityOptions = [
    { label: 'Passport', value: 'PASSPORT' },
    { label: "Driver's License", value: 'DRIVERS_LICENSE' },
    { label: 'School ID', value: 'SCHOOL_ID' },
    { label: 'Government ID', value: 'GOVERNMENT_ID' },
  ];

  const workOptions = [
    { label: 'US Citizen', value: 'US_CITIZEN' },
    { label: 'Permanent Resident', value: 'PERMANENT_RESIDENT' },
    {
      label: 'Employment Authorization Document',
      value: 'EMPLOYMENT_AUTHORIZATION_DOCUMENT',
    },
    { label: 'Asylum', value: 'ASYLUM' },
    {
      label: 'Temporary Protected Status',
      value: 'TEMPORARY_PROTECTED_STATUS',
    },
    { label: 'DACA', value: 'DACA' },
    { label: 'International Student', value: 'INTERNATIONAL_STUDENT' },
    { label: 'Work Visa', value: 'WORK_VISA' },
    {
      label: 'Dependent Visa With Work Authorization',
      value: 'DEPENDENT_VISA_WITH_WORK_AUTH',
    },
    { label: 'Not Authorized', value: 'NOT_AUTHORIZED' },
    { label: 'Other', value: 'OTHER' },
  ];

  const workCapabilityOptions = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' },
    { label: 'Conditional', value: 'CONDITIONAL' },
  ];

  const genderOptions = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Other', value: 'OTHER' },
  ];

  const taxIdTypeOptions = [
    { label: 'SSN', value: 'SSN' },
    { label: 'EIN', value: 'EIN' },
    { label: 'ITIN', value: 'ITIN' },
    { label: 'TIN', value: 'TIN' },
    { label: 'Generic Tax ID', value: 'GENERIC_TAX_ID' },
    { label: 'NINO', value: 'NINO' },
    { label: 'SIN', value: 'SIN' },
    { label: 'BN', value: 'BN' },
    { label: 'PAN', value: 'PAN' },
    { label: 'TFN', value: 'TFN' },
    { label: 'ABN', value: 'ABN' },
    { label: 'VAT', value: 'VAT' },
    { label: 'CPF', value: 'CPF' },
    { label: 'CNPJ', value: 'CNPJ' },
    { label: 'RFC', value: 'RFC' },
    { label: 'PIN', value: 'PIN' },
    { label: 'Other', value: 'OTHER' },
  ];

  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Disabled', value: 'DISABLED' },
    { label: 'Suspended', value: 'SUSPENDED' },
    { label: 'Deleted', value: 'DELETED' },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {uploading && <Alert type="info" showIcon message="Uploading files..." />}
      {uploadError && <Alert type="error" showIcon message={uploadError} />}

      <AntCard size="small" style={{ borderRadius: 12 }}>
        <AntRow gutter={[16, 16]}>
          <AntCol xs={24} md={12}>
            <EditableField
              label="Date of Birth"
              name="birthDate"
              type="date"
              value={formData.birthDate || ''}
              error={errors.birthDate}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.birthDate}
              isSaving={savingFields.birthDate}
              isEditMode={isEditMode}
              placeholder="YYYY-MM-DD"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Gender"
              name="gender"
              type="select"
              options={genderOptions}
              value={formData.gender || ''}
              error={errors.gender}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.gender}
              isSaving={savingFields.gender}
              isEditMode={isEditMode}
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Status"
              name="status"
              type="select"
              options={statusOptions}
              value={formData.status || ''}
              error={errors.status}
              onChange={handleInputChange}
              onSave={null}
              originalValue={originalValues.status}
              isSaving={savingFields.status}
              isEditMode={false}
              disabled={isEditMode}
            />
          </AntCol>
        </AntRow>
      </AntCard>

      <AntCard size="small" style={{ borderRadius: 12 }}>
        <AntRow gutter={[16, 16]}>
          <AntCol xs={24} md={12}>
            <EditableField
              label="Work Email"
              name="workEmail"
              type="email"
              value={formData.workEmail || ''}
              error={errors.workEmail}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.workEmail}
              isSaving={savingFields.workEmail}
              isEditMode={isEditMode}
              placeholder="example@work.com"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Personal Email"
              name="personalEmail"
              type="email"
              value={formData.personalEmail || ''}
              error={errors.personalEmail}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.personalEmail}
              isSaving={savingFields.personalEmail}
              isEditMode={isEditMode}
              placeholder="example@personal.com"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Website"
              name="website"
              type="url"
              value={formData.website || ''}
              error={errors.website}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.website}
              isSaving={savingFields.website}
              isEditMode={isEditMode}
              placeholder="https://yourwebsite.com"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="LinkedIn"
              name="linkedin"
              type="text"
              value={formData.linkedin || ''}
              error={errors.linkedin}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.linkedin}
              isSaving={savingFields.linkedin}
              isEditMode={isEditMode}
              placeholder="linkedin.com/in/username"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Twitter"
              name="twitter"
              type="text"
              value={formData.twitter || ''}
              error={errors.twitter}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.twitter}
              isSaving={savingFields.twitter}
              isEditMode={isEditMode}
              placeholder="@username"
            />
          </AntCol>

          <AntCol xs={24} md={12}>
            <EditableField
              label="Facebook"
              name="facebook"
              type="text"
              value={formData.facebook || ''}
              error={errors.facebook}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues.facebook}
              isSaving={savingFields.facebook}
              isEditMode={isEditMode}
              placeholder="facebook.com/username"
            />
          </AntCol>
        </AntRow>
      </AntCard>
    </Space>
  );
};

export default PersonalForm;
